import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dropdown from '../../../../../components/Dropdown';
import { useScenarios, useScenario } from '../../../../../hooks/siembagpt';
import { confirmedScenarioFields, scenarioFieldsFromSavedRow } from '../utils/scenario';
import ScenarioStatusBadge from './ScenarioStatusBadge';

/**
 * CI/CD saved-scenario picker (ROAR-4511, Business Scenario as a CI/CD test type).
 *
 * A chat-independent alternative to `DefineScenarioSection`'s guardrail define/validate loop: the
 * headless CI/CD config has no `chat_id`/`nonce`, so a scenario cannot be *authored* there — it is
 * *referenced* from the asset's saved scenarios (`genpt_scenarios`, authored earlier in chat).
 * Selecting one auto-confirms (there is no navigation replay to gate on) and emits the exact
 * `onChange({ confirmed, ...confirmedScenarioFields })` shape `DefineScenarioSection` emits, so it is a
 * drop-in inside `TestSetup` — all of `TestSetup`'s scenario gating/submit machinery keeps working.
 *
 * @param {number|string|null} assetId Asset whose saved scenarios populate the picker.
 * @param {object|null} initialScenario Restore snapshot `{ scenarioId, description, featureName, steps, confirmed }`.
 * @param {boolean} isDisabled Disables selection (mirrors DefineScenarioSection).
 * @param {boolean} isReadOnly Read-only summary render (e.g. "View Setup Details"); no network calls.
 * @param {(state: object) => void} onChange Called with `{ confirmed, scenarioId, ... }` on every change.
 */
export default function SavedScenarioPicker({
  assetId = null,
  initialScenario = null,
  isDisabled = false,
  isReadOnly = false,
  onChange = () => {},
}) {
  // `initialScenario` is rebuilt inline by TestSetup on every render (new object identity), so capture
  // it once — reading it in the emit effect below would otherwise loop (emit → setState → re-render).
  const initialRef = useRef(initialScenario);
  const [selectedId, setSelectedId] = useState(
    initialScenario?.scenarioId != null ? String(initialScenario.scenarioId) : null
  );

  const { data: scenariosData } = useScenarios(assetId, { enabled: !isReadOnly });
  const { data: scenarioDetail } = useScenario(selectedId, {
    enabled: !isReadOnly && !!selectedId,
  });

  const scenarioOptions = useMemo(
    () =>
      (scenariosData?.scenarios || [])
        .filter((scenario) => scenario.name || scenario.feature_name)
        .map((scenario) => ({
          label: scenario.name || scenario.feature_name,
          value: scenario.id,
          id: scenario.id,
          name: scenario.name || scenario.feature_name,
          badge: <ScenarioStatusBadge status={scenario.last_run_status} />,
        })),
    [scenariosData]
  );

  // Best available source for the current selection: freshly-fetched detail (has steps) > the list
  // row > the restore snapshot (so the summary/gate settle before the detail request resolves).
  const resolvedSource = useMemo(() => {
    if (!selectedId) return null;
    const detailRow = scenarioDetail?.scenario;
    if (detailRow) return detailRow;
    const listRow = (scenariosData?.scenarios || []).find(
      (s) => String(s.id) === String(selectedId)
    );
    if (listRow) return listRow;
    const init = initialRef.current;
    if (init && String(init.scenarioId) === String(selectedId)) {
      return {
        scenario_id: init.scenarioId,
        description: init.description,
        feature_name: init.featureName,
        steps: init.steps,
      };
    }
    return null;
  }, [selectedId, scenarioDetail, scenariosData]);

  // Notify TestSetup on every change so it can gate Continue/NEXT and fold the fields into configData.
  useEffect(() => {
    if (isReadOnly) return;
    if (!resolvedSource) {
      onChange({ confirmed: false, ...confirmedScenarioFields({}) });
      return;
    }
    onChange({ confirmed: true, ...scenarioFieldsFromSavedRow(resolvedSource) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedSource, isReadOnly]);

  if (isReadOnly) {
    const featureName = initialScenario?.featureName || '';
    const description = initialScenario?.description || '';
    const steps = Array.isArray(initialScenario?.steps) ? initialScenario.steps : [];
    return (
      <div className="mt-4">
        <div className="tracking-wide font-semibold text-black mb-1">Business Scenario</div>
        <div className="text-sm">{featureName || 'Scenario not yet confirmed'}</div>
        {description && <div className="text-sm opacity-70 mt-1">{description}</div>}
        {steps.length > 0 && (
          <div className="mt-3 space-y-2">
            {steps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="shrink-0 rounded-[10px] bg-primary-50 text-primary-500 px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
                  Step {idx + 1}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const description = resolvedSource?.description ?? '';
  const steps = Array.isArray(resolvedSource?.steps) ? resolvedSource.steps : [];

  return (
    <div className="mt-4">
      <div className="tracking-wide font-semibold text-black mb-1">Business Scenario</div>
      <div className="text-sm opacity-70 mb-2">
        Select a saved scenario to run in this pipeline. Scenarios are created in the GenPT chat
        flow.
      </div>
      <Dropdown
        options={scenarioOptions}
        placeholder="Select Scenario"
        name="scenario"
        isMulti={false}
        value={scenarioOptions.find((o) => String(o.value) === String(selectedId)) || null}
        onChange={(option) => setSelectedId(option?.value != null ? String(option.value) : null)}
        isDisabled={isDisabled}
      />
      {description && <div className="text-sm opacity-70 mt-3">{description}</div>}
      {steps.length > 0 && (
        <div className="mt-3 space-y-2">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm">
              <span className="shrink-0 rounded-[10px] bg-primary-50 text-primary-500 px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
                Step {idx + 1}
              </span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
